import React, { useState, useEffect } from 'react';
import { Product, ProductVariant, ProductVariantModel } from '../../types';
import { X, Save, AlertCircle, Eye, Upload, Trash2, Plus, Zap, DollarSign, Tag, ImageIcon, Smartphone } from 'lucide-react';
import { storageService } from '../../services/StorageService';
import { deviceService } from '../../services/DeviceService';
import { useToast } from '../../context/ToastContext';
import { SearchableSelect } from './SearchableSelect';
import { compressImage } from '../../utils/imageOptimizer';
import { ConfirmDialog } from './ConfirmDialog';
import { generateSlug } from '../../utils/textUtils';

interface EditProductModalProps {
    product: Product | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (id: string, updates: Partial<Product>) => Promise<void>;
    onCreate?: (product: Partial<Product>) => Promise<void>;
    onDelete?: (id: string) => Promise<void>;
    availableOptions: { categories: string[], makes: string[], models: string[] };
    userRole?: string;
}

export const EditProductModal: React.FC<EditProductModalProps> = ({ product, isOpen, onClose, onSave, onCreate, onDelete, availableOptions, userRole = 'admin' }) => {
    const { showToast } = useToast();
    const [formData, setFormData] = useState<Partial<Product>>({});
    const [variants, setVariants] = useState<Partial<ProductVariant>[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [imageList, setImageList] = useState<string[]>([]);
    const [touchDragIndex, setTouchDragIndex] = useState<number | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // New Variant Creation State
    const [isAddingVariant, setIsAddingVariant] = useState(false);
    const [newVariantData, setNewVariantData] = useState({
        color_name: '',
        color_code: '',
        color_hex: '#000000',
        models: [] as string[]
    });
    const [availableModelsForBrand, setAvailableModelsForBrand] = useState<string[]>(availableOptions.models);

    useEffect(() => {
        const fetchModels = async () => {
            const currentBrandName = formData.brand_id || product?.make || '';
            const normalizedBrandName = availableOptions.makes.find(m => m === currentBrandName) || product?.make || '';

            if (!normalizedBrandName) {
                setAvailableModelsForBrand([]); // Lock if no brand
                return;
            }

            const nameToFetch = normalizedBrandName.toLowerCase().includes('apple') ? 'Apple' :
                normalizedBrandName.toLowerCase().includes('samsung') ? 'Samsung' : '';
            if (nameToFetch) {
                const families = await deviceService.getFamiliesByBrand(nameToFetch as 'Apple' | 'Samsung');
                if (families.length > 0) {
                    const models = families.flatMap(f => f.variants || []);
                    setAvailableModelsForBrand(models.length > 0 ? models : (nameToFetch === 'Apple' ? FALLBACK_APPLE_MODELS : FALLBACK_SAMSUNG_MODELS));
                } else {
                    setAvailableModelsForBrand(nameToFetch === 'Apple' ? FALLBACK_APPLE_MODELS : FALLBACK_SAMSUNG_MODELS);
                }
            } else {
                setAvailableModelsForBrand([]);
            }
        };
        fetchModels();
    }, [formData.brand_id, product?.make, availableOptions.makes]);

    const FALLBACK_APPLE_MODELS = [
        "iPhone 11", "iPhone 11 Pro", "iPhone 11 Pro Max",
        "iPhone 12", "iPhone 12 Pro", "iPhone 12 Pro Max",
        "iPhone 13", "iPhone 13 Pro", "iPhone 13 Pro Max",
        "iPhone 14", "iPhone 14 Plus", "iPhone 14 Pro", "iPhone 14 Pro Max",
        "iPhone 15", "iPhone 15 Plus", "iPhone 15 Pro", "iPhone 15 Pro Max",
        "iPhone 16", "iPhone 16 Plus", "iPhone 16 Pro", "iPhone 16 Pro Max",
        "iPhone 17", "iPhone Air", "iPhone 17 Pro", "iPhone 17 Pro Max",
    ];

    const FALLBACK_SAMSUNG_MODELS = [
        "Galaxy S21", "Galaxy S21+", "Galaxy S21 Ultra", "Galaxy S21 FE",
        "Galaxy S22", "Galaxy S22+", "Galaxy S22 Ultra",
        "Galaxy S23", "Galaxy S23+", "Galaxy S23 Ultra", "Galaxy S23 FE",
        "Galaxy S24", "Galaxy S24+", "Galaxy S24 Ultra", "Galaxy S24 FE",
        "Galaxy S25", "Galaxy S25+", "Galaxy S25 Ultra",
        "Galaxy Z Fold 4", "Galaxy Z Fold 5", "Galaxy Z Fold 6",
        "Galaxy Z Flip 4", "Galaxy Z Flip 5", "Galaxy Z Flip 6",
    ];

    // Helper para generar SKU aleatorio: BASE-SKU
    const generateRandomSKU = () => {
        const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();
        return `CV-${randomPart}`;
    };
    const [showSafeZones, setShowSafeZones] = useState(true);

    const isCreateMode = !product?.id;

    const handleTouchStart = (index: number) => {
        setTouchDragIndex(index);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (touchDragIndex !== null) {
            if (e.cancelable) e.preventDefault();
        }
    };

    const handleTouchEnd = (e: React.TouchEvent, toIndex: number) => {
        if (touchDragIndex === null || touchDragIndex === toIndex) {
            setTouchDragIndex(null);
            return;
        }

        const newList = [...imageList];
        const [movedItem] = newList.splice(touchDragIndex, 1);
        newList.splice(toIndex, 0, movedItem);
        setImageList(newList);
        setTouchDragIndex(null);
    };

    useEffect(() => {
        if (isOpen) {
            if (product && product.id) {
                // Modo Edición
                setFormData({
                    name: product.name,
                    slug: product.slug,
                    description: product.description,
                    base_sku: product.base_sku || product.name?.substring(0, 3).toUpperCase(),
                    category_id: product.category_id,
                    brand_id: product.brand_id,
                    image: product.image,
                    images: product.images || [],
                    is_premium: product.is_premium,
                    is_magsafe: product.is_magsafe,
                    is_last_units: product.is_last_units,
                    is_active: product.is_active ?? true
                });
                setImageList(product.images && product.images.length > 0 ? product.images : (product.image ? [product.image] : []));
                setVariants(product.variants || []);
                setPreviewUrl(null);
            } else {
                // Modo Creación
                setFormData({
                    base_sku: generateRandomSKU(),
                    is_active: true
                });
                setImageList([]);
                setVariants([]);
                setPreviewUrl(null);
            }
            setIsAddingVariant(false);
            setNewVariantData({ color_name: '', color_code: '', color_hex: '#000000', models: [] });
        }
    }, [product, isOpen]);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    // -- Variant Management Logic --

    const STANDARD_COLORS = [
        { name: 'Aramid', code: 'ARMD', hex: '#DAA520', label: 'Aramid (Dorado)' },
        { name: 'Black', code: 'BLK', hex: '#000000', label: 'Black' },
        { name: 'Blue', code: 'BLU', hex: '#1E3A8A', label: 'Blue' },
        { name: 'Dark Grey', code: 'DGRY', hex: '#A9A9A9', label: 'Dark Grey' },
        { name: 'Gold', code: 'GLD', hex: '#FFD700', label: 'Gold' },
        { name: 'Green', code: 'GRN', hex: '#008000', label: 'Green' },
        { name: 'Infinity', code: 'INF', hex: '#121026', label: 'Infinity' },
        { name: 'Light Grey', code: 'LGRY', hex: '#D3D3D3', label: 'Light Grey' },
        { name: 'Pink', code: 'PNK', hex: '#FFC0CB', label: 'Pink' },
        { name: 'Purple', code: 'PUR', hex: '#800080', label: 'Purple' },
        { name: 'Red', code: 'RED', hex: '#FF0000', label: 'Red' },
        { name: 'Silver', code: 'SLV', hex: '#C0C0C0', label: 'Silver' },
        { name: 'Sin Color', code: 'SCOL', hex: '#000000', label: 'Sin Color' },
        { name: 'Smoked', code: 'SMK', hex: '#2A2A2A', label: 'Smoked' },
    ];

    const handleCreateVariant = () => {
        if (!newVariantData.color_name) {
            showToast('Ingresa un nombre de color', 'error');
            return;
        }
        if (newVariantData.models.length === 0) {
            showToast('Selecciona al menos un modelo', 'error');
            return;
        }

        const colorCode = newVariantData.color_code || newVariantData.color_name.substring(0, 3).toUpperCase();

        const newVariant: Partial<ProductVariant> = {
            id: `temp-${Date.now()}`,
            color_name: newVariantData.color_name,
            color_code: colorCode,
            color_hex: newVariantData.color_hex,
            variant_sku: `${formData.base_sku || 'SKU'}-${colorCode}`,
            image_url: '',
            texture_url: '',
            is_active: true,
            models: newVariantData.models.map(m => ({
                id: `temp-model-${Date.now()}-${Math.random()}`,
                variant_id: '',
                model_id: m,
                model_name: m, // Using ID as name fallback for new items
                in_stock: true,
                is_active: true
            }))
        };

        setVariants([...variants, newVariant]);
        setIsAddingVariant(false);
        setNewVariantData({ color_name: '', color_code: '', color_hex: '#000000', models: [] });
    };

    const updateVariant = (id: string, updates: Partial<ProductVariant>) => {
        setVariants(prev => prev.map(v => v.id === id ? { ...v, ...updates } : v));
    };

    const removeVariant = (id: string) => {
        setVariants(prev => prev.filter(v => v.id !== id));
    };

    const updateVariantModel = (variantId: string, modelId: string, updates: Partial<ProductVariantModel>) => {
        setVariants(prev => prev.map(v => {
            if (v.id === variantId) {
                return {
                    ...v,
                    models: (v.models || []).map(m => m.model_id === modelId ? { ...m, ...updates } : m)
                };
            }
            return v;
        }));
    };

    const removeVariantModel = (variantId: string, modelId: string) => {
        setVariants(prev => prev.map(v => {
            if (v.id === variantId) {
                return {
                    ...v,
                    models: (v.models || []).filter(m => m.model_id !== modelId)
                };
            }
            return v;
        }));
    };

    const addModelToVariant = (variantId: string, modelId: string) => {
        setVariants(prev => prev.map(v => {
            if (v.id === variantId) {
                const exists = (v.models || []).find(m => m.model_id === modelId);
                if (exists) {
                    showToast('Este modelo ya existe en esta variante', 'error');
                    return v;
                }
                const newModel: ProductVariantModel = {
                    id: `temp-model-${Date.now()}`,
                    variant_id: variantId,
                    model_id: modelId,
                    model_name: modelId, // Use ID as generic fallback
                    in_stock: true,
                    is_active: true
                };
                return { ...v, models: [...(v.models || []), newModel] };
            }
            return v;
        }));
    };

    const handleVariantImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, variantId: string) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const processedFile = await compressImage(file, { maxWidth: 800, quality: 0.8 });
            const publicUrl = await storageService.uploadImage(processedFile, 'products');
            updateVariant(variantId, { image_url: publicUrl });
            showToast('Imagen de la variante actualizada', 'success');
        } catch (error: any) {
            console.error(error);
            showToast('Error al subir imagen', 'error');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleVariantTextureUpload = async (e: React.ChangeEvent<HTMLInputElement>, variantId: string) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            // Textures don't need to be huge, compressing even more
            const processedFile = await compressImage(file, { maxWidth: 400, quality: 0.7 });
            const publicUrl = await storageService.uploadImage(processedFile, 'textures');
            updateVariant(variantId, { texture_url: publicUrl });
            showToast('Textura de la variante actualizada', 'success');
        } catch (error: any) {
            console.error(error);
            showToast('Error al subir textura', 'error');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleModelImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, variantId: string, modelId: string) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const processedFile = await compressImage(file, { maxWidth: 800, quality: 0.8 });
            const publicUrl = await storageService.uploadImage(processedFile, 'products');
            updateVariantModel(variantId, modelId, { image_url: publicUrl });
            showToast('Imagen del modelo actualizada', 'success');
        } catch (error: any) {
            console.error(error);
            showToast('Error al subir imagen del modelo', 'error');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleRemoveModelImage = (variantId: string, modelId: string) => {
        updateVariantModel(variantId, modelId, { image_url: undefined }); // Remove image
    };

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
        e.preventDefault();

        if (!formData.name?.trim()) {
            showToast('El nombre del producto es obligatorio', 'error');
            return;
        }
        if (!formData.category_id) {
            showToast('Debes seleccionar una categoría', 'error');
            return;
        }
        if (!formData.brand_id) {
            showToast('Debes seleccionar una marca', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const updates = {
                ...formData,
                images: imageList,
                image: imageList.length > 0 ? imageList[0] : '',
                variants: variants as ProductVariant[]
            };

            if (isCreateMode && onCreate) {
                await onCreate(updates);
                showToast('Producto creado correctamente', 'success');
            } else if (product?.id) {
                await onSave(product.id, updates);
                showToast('Producto actualizado correctamente', 'success');
            }
            onClose();
        } catch (error: any) {
            console.error('Error saving product:', error);
            showToast('Error al guardar el producto', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (imageList.length >= 6) {
            showToast('Has alcanzado el límite de 6 imágenes por producto', 'warning');
            return;
        }

        setIsUploading(true);
        try {
            const processedFile = await compressImage(file, { maxWidth: 1200, quality: 0.8 });
            const publicUrl = await storageService.uploadImage(processedFile, 'products');
            setImageList(prev => [...prev, publicUrl]);
            showToast('Imagen subida correctamente', 'success');
        } catch (error: any) {
            showToast(error.message || 'Error al subir la imagen', 'error');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleReplaceImage = async (e: React.ChangeEvent<HTMLInputElement>, indexToReplace: number) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const processedFile = await compressImage(file, { maxWidth: 1200, quality: 0.8 });
            const publicUrl = await storageService.uploadImage(processedFile, 'products');
            setImageList(prev => {
                const newList = [...prev];
                newList[indexToReplace] = publicUrl;
                return newList;
            });
            showToast('Imagen reemplazada correctamente', 'success');
        } catch (error: any) {
            showToast(error.message || 'Error al reemplazar la imagen', 'error');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleRemoveImage = (index: number) => {
        setImageList(prev => prev.filter((_, i) => i !== index));
    };

    // Find the original brand and category names to properly initialize SearchableSelect
    // since we use names in SearchableSelect for now
    const selectedBrandName = availableOptions.makes.find(m => {
        return m === product?.make; // Fallback mapping
    }) || product?.make || '';

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in duration-200 flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10 shrink-0">
                    <h2 className="text-xl font-bold uppercase italic text-white flex items-center gap-2">
                        {isCreateMode ? (
                            <><span className="text-white">NUEVO</span> PRODUCTO</>
                        ) : (
                            <><span className="text-white">EDITAR</span> {product?.name}</>
                        )}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full text-zinc-400 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
                    <form id="product-form" onSubmit={handleSubmit} className="space-y-6">
                        {/* Main Info */}
                        <div className="space-y-4">
                            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                                <div className="flex-1">
                                    <label className="text-[10px] uppercase font-bold text-white tracking-wider">Nombre del Producto</label>
                                    <input
                                        type="text"
                                        value={formData.name || ''}
                                        onChange={e => setFormData({ ...formData, name: e.target.value, slug: generateSlug(e.target.value) })}
                                        className="w-full bg-black border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/50 text-white p-3 rounded focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200 outline-none font-bold"
                                    />
                                </div>
                                <div className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 px-4 py-2.5 rounded-sm">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase font-bold text-white tracking-wider">Aviso Urgencia</span>
                                        <span className="text-[8px] text-zinc-500 uppercase tracking-tighter">¡Últimas Unidades!</span>
                                    </div>
                                    <label className={`relative inline-flex items-center ${(userRole === 'admin' || userRole === 'marketing') ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                        <input
                                            type="checkbox"
                                            checked={formData.is_last_units || false}
                                            onChange={(e) => (userRole === 'admin' || userRole === 'marketing') && setFormData({ ...formData, is_last_units: e.target.checked })}
                                            className="sr-only peer"
                                            disabled={!(userRole === 'admin' || userRole === 'marketing')}
                                        />
                                        <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-zinc-400 peer-checked:after:bg-white after:border-zinc-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600 transition-colors"></div>
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] uppercase font-bold text-white tracking-wider">SKU Base</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={formData.base_sku || ''}
                                            readOnly
                                            className="w-full bg-black border border-zinc-800 text-zinc-500 p-2 rounded font-mono text-sm uppercase cursor-not-allowed select-none"
                                            title="El SKU es autogenerado"
                                        />
                                        {isCreateMode && (
                                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                                <Zap size={14} className="text-white opacity-50" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] uppercase font-bold text-white tracking-wider">Marca</label>
                                    <SearchableSelect
                                        value={(availableOptions.makes.find(m => m === formData.brand_id)) || (availableOptions.makes.find(m => m === product?.make)) || formData.brand_id || product?.make || ''}
                                        onChange={(val) => setFormData({ ...formData, brand_id: val as string })}
                                        options={availableOptions.makes}
                                        placeholder="Seleccionar Marca"
                                        className="w-full"
                                        disabled={userRole !== 'admin'}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] uppercase font-bold text-white tracking-wider">Categoría</label>
                                    <SearchableSelect
                                        value={(availableOptions.categories.find(c => c === formData.category_id)) || (availableOptions.categories.find(c => c === product?.category)) || formData.category_id || product?.category || ''}
                                        onChange={(val) => setFormData({ ...formData, category_id: val as string })}
                                        options={availableOptions.categories}
                                        placeholder="Seleccionar Categoría"
                                        className="w-full"
                                        disabled={userRole !== 'admin'}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] uppercase font-bold text-white tracking-wider">Especificaciones</label>
                                <textarea
                                    value={formData.description || ''}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full bg-black border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/50 text-zinc-300 p-3 rounded focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200 outline-none text-sm min-h-[80px] resize-none"
                                />
                            </div>
                        </div>

                        {/* Image Gallery */}
                        <div className="space-y-3">
                            <label className="text-[10px] uppercase font-bold text-white tracking-wider">Galería del Producto (General)</label>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 md:gap-4">
                                {imageList.map((img, index) => (
                                    <div
                                        key={index}
                                        draggable
                                        // ... (Drag functionality kept minimal for readability)
                                        className={`aspect-square bg-black border rounded relative group overflow-hidden cursor-move touch-none transition-all ${index === 0 ? 'border-white ring-1 ring-white' : 'border-zinc-800'}`}
                                    >
                                        <img src={img} alt={`Product ${index}`} className="w-full h-full object-cover transition-opacity duration-300 md:group-hover:opacity-60" />
                                        <div className="absolute top-1 right-1 flex flex-col gap-1 z-20 pointer-events-auto">
                                            <label className="bg-black/60 hover:bg-white text-white hover:text-black p-1.5 md:p-2 rounded-full cursor-pointer transition-all backdrop-blur-sm shadow-lg border border-white/10" onClick={(e) => e.stopPropagation()}>
                                                <Upload size={14} />
                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleReplaceImage(e, index)} />
                                            </label>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }} className="bg-black/60 hover:bg-red-500 text-white p-1.5 md:p-2 rounded-full transition-all backdrop-blur-sm shadow-lg border border-white/10">
                                                <X size={14} />
                                            </button>
                                        </div>
                                        {index === 0 && <div className="absolute bottom-0 left-0 right-0 bg-white/90 text-black text-[8px] md:text-[9px] font-bold text-center py-0.5 md:py-1 uppercase backdrop-blur-sm">PRINCIPAL</div>}
                                    </div>
                                ))}
                                <label className={`aspect-square bg-black border border-dashed border-zinc-700 rounded flex flex-col items-center justify-center gap-1 md:gap-2 cursor-pointer hover:bg-zinc-900 hover:border-zinc-600 transition-all group ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <Upload size={16} className="text-zinc-500 group-hover:text-white" />
                                    <span className="text-[8px] md:text-[10px] uppercase font-bold text-zinc-500 group-hover:text-white text-center px-1">Subir</span>
                                    <input type="file" accept="image/*" onChange={handleFileUpload} disabled={isUploading} className="hidden" />
                                </label>
                            </div>
                        </div>

                        {/* Status Toggles */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                            <label className={`flex items-center justify-between p-3 md:p-4 rounded border transition-all select-none ${(userRole === 'admin' || userRole === 'marketing') ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'} ${formData.is_premium ? 'bg-green-500/10 border-green-500/50' : 'bg-black border-zinc-800 hover:border-zinc-700'}`}>
                                <span className={`text-xs md:text-sm font-bold uppercase flex items-center gap-2 ${formData.is_premium ? 'text-green-500' : 'text-zinc-500'}`}>
                                    <DollarSign size={14} className={formData.is_premium ? 'text-green-500' : 'text-zinc-600'} />
                                    PREMIUM
                                </span>
                                <input type="checkbox" disabled={!(userRole === 'admin' || userRole === 'marketing')} checked={formData.is_premium || false} onChange={e => (userRole === 'admin' || userRole === 'marketing') && setFormData({ ...formData, is_premium: e.target.checked })} className="hidden" />
                                <div className={`w-8 h-4 md:w-10 md:h-5 rounded-full flex items-center p-0.5 md:p-1 transition-colors duration-300 shrink-0 ${formData.is_premium ? 'bg-green-500' : 'bg-zinc-800'}`}>
                                    <div className={`w-3 h-3 rounded-full bg-black shadow-sm transition-transform duration-300 ${formData.is_premium ? 'translate-x-3 md:translate-x-4' : 'translate-x-0'}`} />
                                </div>
                            </label>

                            <label className={`flex items-center justify-between p-3 md:p-4 rounded border transition-all select-none ${(userRole === 'admin' || userRole === 'marketing') ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'} ${(formData.is_active ?? true) ? 'bg-blue-500/10 border-blue-500/50' : 'bg-black border-zinc-800 hover:border-zinc-700'}`}>
                                <span className={`text-xs md:text-sm font-bold uppercase flex items-center gap-2 ${(formData.is_active ?? true) ? 'text-blue-500' : 'text-zinc-500'}`}>
                                    <Eye size={14} className={(formData.is_active ?? true) ? 'text-blue-500' : 'text-zinc-600'} />
                                    VISIBLE
                                </span>
                                <input type="checkbox" disabled={!(userRole === 'admin' || userRole === 'marketing')} checked={formData.is_active ?? true} onChange={e => (userRole === 'admin' || userRole === 'marketing') && setFormData({ ...formData, is_active: e.target.checked })} className="hidden" />
                                <div className={`w-8 h-4 md:w-10 md:h-5 rounded-full flex items-center p-0.5 md:p-1 transition-colors duration-300 shrink-0 ${(formData.is_active ?? true) ? 'bg-blue-500' : 'bg-zinc-800'}`}>
                                    <div className={`w-3 h-3 rounded-full bg-black shadow-sm transition-transform duration-300 ${(formData.is_active ?? true) ? 'translate-x-3 md:translate-x-4' : 'translate-x-0'}`} />
                                </div>
                            </label>

                            <label className={`flex items-center justify-between p-3 md:p-4 rounded border transition-all select-none ${(userRole === 'admin' || userRole === 'marketing') ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'} ${formData.is_magsafe ? 'bg-purple-500/10 border-purple-500/50' : 'bg-black border-zinc-800 hover:border-zinc-700'}`}>
                                <span className={`text-xs md:text-sm font-bold uppercase flex items-center gap-2 ${formData.is_magsafe ? 'text-purple-400' : 'text-zinc-500'}`}>
                                    <Zap size={14} className={formData.is_magsafe ? 'text-purple-400' : 'text-zinc-600'} />
                                    MAGSAFE
                                </span>
                                <input type="checkbox" disabled={!(userRole === 'admin' || userRole === 'marketing')} checked={formData.is_magsafe || false} onChange={e => (userRole === 'admin' || userRole === 'marketing') && setFormData({ ...formData, is_magsafe: e.target.checked })} className="hidden" />
                                <div className={`w-8 h-4 md:w-10 md:h-5 rounded-full flex items-center p-0.5 md:p-1 transition-colors duration-300 shrink-0 ${formData.is_magsafe ? 'bg-purple-500' : 'bg-zinc-800'}`}>
                                    <div className={`w-3 h-3 rounded-full bg-black shadow-sm transition-transform duration-300 ${formData.is_magsafe ? 'translate-x-3 md:translate-x-4' : 'translate-x-0'}`} />
                                </div>
                            </label>
                        </div>

                        {/* Variants Management - NEW UI */}
                        {(formData.brand_id || product?.make) && formData.category_id ? (
                            <div className="border border-zinc-800 rounded-lg bg-black/20 relative z-40">
                                <div className="bg-zinc-950 p-3 rounded-t-lg border-b border-zinc-800 flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-white uppercase flex items-center gap-2">
                                        <Tag size={14} className="text-white" />
                                        Variantes (Color/Textura) Y Modelos
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setShowSafeZones(!showSafeZones)}
                                            className={`text-[9px] font-bold uppercase px-2 py-1.5 rounded border transition-all ${showSafeZones ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'text-zinc-500 border-zinc-800 hover:border-zinc-700'
                                                }`}
                                            title="Mostrar zonas seguras del catálogo"
                                        >
                                            {showSafeZones ? 'Guías On' : 'Guías Off'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsAddingVariant(true)}
                                            className="text-[10px] bg-white text-black px-3 py-1.5 rounded uppercase font-bold flex items-center gap-1 hover:bg-zinc-200 transition-colors"
                                        >
                                            <Plus size={12} /> Añadir Variante
                                        </button>
                                    </div>
                                </div>

                                <div className="p-4 space-y-4">
                                    {variants.length === 0 && !isAddingVariant && (
                                        <div className="text-center py-8 text-zinc-500 italic text-sm border-2 border-dashed border-zinc-800 rounded-lg">
                                            No hay variantes. Añade un color/textura para seleccionar los modelos disponibles.
                                        </div>
                                    )}

                                    {variants.map((v) => (
                                        <div key={v.id} className="bg-zinc-900 border border-zinc-800 rounded-lg flex flex-col md:flex-row">
                                            {/* Left: General Variant Specs */}
                                            <div className="p-4 border-b md:border-b-0 md:border-r border-zinc-800 w-full md:w-1/3 space-y-4 bg-zinc-900/50 rounded-t-lg md:rounded-l-lg md:rounded-tr-none">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] uppercase font-bold text-zinc-500">APARIENCIA (COLOR/TEXTURA)</label>
                                                    <select
                                                        value={v.color_name}
                                                        onChange={(e) => {
                                                            const selected = STANDARD_COLORS.find(c => c.name === e.target.value);
                                                            if (selected) {
                                                                updateVariant(v.id!, {
                                                                    color_name: selected.name,
                                                                    color_hex: selected.hex,
                                                                    color_code: selected.code,
                                                                    variant_sku: `${formData.base_sku || 'SKU'}-${selected.code}`
                                                                });
                                                            } else {
                                                                updateVariant(v.id!, { color_name: e.target.value });
                                                            }
                                                        }}
                                                        className="w-full bg-black border border-zinc-700 rounded p-2 text-white text-sm font-bold focus:border-white outline-none mb-2"
                                                    >
                                                        <option value="" disabled>Seleccione Color/Textura...</option>
                                                        {STANDARD_COLORS.map(c => <option key={c.name} value={c.name}>{c.label}</option>)}
                                                    </select>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 bg-zinc-900 border border-zinc-700 p-2 rounded text-zinc-400 text-sm flex items-center justify-between">
                                                            <span>Código: {v.color_code || '---'}</span>
                                                            <label className="relative block w-6 h-6 rounded-sm border border-zinc-600 cursor-pointer group overflow-hidden" title="Subir Textura">
                                                                {v.texture_url ? (
                                                                    <img src={v.texture_url} alt="Textura" className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <div className="w-full h-full" style={{ backgroundColor: v.color_hex || '#000000' }} />
                                                                )}
                                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleVariantTextureUpload(e, v.id!)} disabled={isUploading} />
                                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-20">
                                                                    <Upload size={10} className="text-white" />
                                                                </div>
                                                            </label>
                                                        </div>
                                                        {v.texture_url && (
                                                            <button 
                                                                type="button" 
                                                                onClick={() => updateVariant(v.id!, { texture_url: '' })} 
                                                                className="text-red-500 hover:text-red-400 bg-zinc-900 p-2 rounded border border-zinc-700 hover:border-red-500 transition-colors"
                                                                title="Quitar Textura"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    <label className="relative block w-16 h-16 bg-black border border-zinc-700 rounded cursor-pointer group hover:border-white transition-colors overflow-hidden shrink-0">
                                                        {v.image_url ? (
                                                            <div className="relative w-full h-full">
                                                                <img src={v.image_url} alt="Variant" className="w-full h-full object-cover" />

                                                                {/* SAFE ZONES OVERLAY (Simulating ProductCard) */}
                                                                {showSafeZones && (
                                                                    <div className="absolute inset-0 pointer-events-none z-10 text-[6px] font-black italic">
                                                                        {/* Top Right (Exclusive) */}
                                                                        <div className="absolute top-1 right-1 w-4 h-2 bg-white/40 border border-white/50 rounded-xs" />
                                                                        {/* Top Left (Sold Out) */}
                                                                        <div className="absolute top-1 left-1 w-4 h-2 bg-red-500/40 border border-red-500/50 rounded-xs" />
                                                                        {/* Bottom Right (Variants) */}
                                                                        <div className="absolute bottom-1 right-1 w-5 h-2 bg-blue-500/40 border border-blue-500/50 rounded-xs" />
                                                                        {/* Hover Overlay Center */}
                                                                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-3 border border-white/40 rounded-full" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-zinc-600">
                                                                <ImageIcon size={20} />
                                                            </div>
                                                        )}
                                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleVariantImageUpload(e, v.id!)} disabled={isUploading} />
                                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-20">
                                                            <Upload size={12} className="text-white" />
                                                        </div>
                                                    </label>
                                                    <div className="flex-1 text-xs text-zinc-500">
                                                        <p>SKU: <span className="text-white font-mono">{v.variant_sku}</span></p>
                                                        <button type="button" onClick={() => removeVariant(v.id!)} className="text-red-500 hover:text-red-400 underline mt-1">Borrar Variante</button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right: Model Matrix List */}
                                            <div className="p-4 w-full md:w-2/3 flex flex-col">
                                                <label className="text-[10px] uppercase font-bold text-zinc-500 mb-2 flex items-center justify-between">
                                                    <span>Modelos en Stock ({(v.models || []).length})</span>
                                                </label>

                                                <div className="flex-1 space-y-1 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar mb-3">
                                                    {(v.models || []).map((m) => (
                                                        <div key={m.model_id} className="flex flex-col bg-black border border-zinc-800 p-2 rounded group/item hover:border-zinc-600 transition-colors gap-2">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2 overflow-hidden flex-1">
                                                                    <Smartphone size={14} className="text-zinc-600 shrink-0" />
                                                                    <span className="text-sm font-bold text-zinc-200 truncate" title={m.model_name || m.model_id}>
                                                                        {m.model_name || m.model_id}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-3 shrink-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-[10px] uppercase font-bold text-zinc-500">Stock</span>
                                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={m.in_stock || false}
                                                                                onChange={(e) => updateVariantModel(v.id!, m.model_id, { in_stock: e.target.checked })}
                                                                                className="sr-only peer"
                                                                            />
                                                                            <div className="w-11 h-6 bg-[#39393D] outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#34C759] shadow-inner transition-colors"></div>
                                                                        </label>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeVariantModel(v.id!, m.model_id)}
                                                                        className="text-zinc-600 hover:text-red-500 transition-colors"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {/* Model Image specific upload */}
                                                            <div className="flex items-center gap-2 pl-6">
                                                                <label className="relative block w-8 h-8 md:w-10 md:h-10 bg-zinc-900 border border-zinc-800 rounded cursor-pointer group hover:border-zinc-600 transition-colors overflow-hidden shrink-0">
                                                                    {m.image_url ? (
                                                                        <img src={m.image_url} alt="Model specific" className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        <div className="w-full h-full flex items-center justify-center text-zinc-700 relative">
                                                                            <ImageIcon size={12} />
                                                                            <Plus size={8} className="absolute bottom-1 right-1" />
                                                                        </div>
                                                                    )}
                                                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleModelImageUpload(e, v.id!, m.model_id)} disabled={isUploading} />
                                                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-20">
                                                                        <Upload size={10} className="text-white" />
                                                                    </div>
                                                                </label>
                                                                <div className="flex-1 flex flex-col justify-center">
                                                                    {m.image_url ? (
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-[9px] text-zinc-400 uppercase">Foto Especial</span>
                                                                            <button type="button" onClick={() => handleRemoveModelImage(v.id!, m.model_id)} className="text-[10px] text-red-500 hover:text-red-400 underline">Quitar imagen</button>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-[9px] text-zinc-600 italic">No requiere, usará foto principal del color.</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {(v.models || []).length === 0 && (
                                                        <div className="text-xs text-zinc-500 italic p-2 text-center border border-dashed border-zinc-800 rounded">Sin modelos asignados a esta variante.</div>
                                                    )}
                                                </div>

                                                <div className="mt-auto pt-2 border-t border-zinc-800/50">
                                                    <SearchableSelect
                                                        options={availableModelsForBrand.length > 0 ? availableModelsForBrand : availableOptions.models}
                                                        value=""
                                                        onChange={(val) => addModelToVariant(v.id!, val as string)}
                                                        placeholder="+ Añadir modelo (Compatibilidad)"
                                                        className="w-full opacity-70 hover:opacity-100 transition-opacity"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add New Variant Form */}
                                    {isAddingVariant && (
                                        <div className="bg-zinc-900/50 border border-zinc-700 border-dashed rounded-lg p-4 animate-in fade-in slide-in-from-bottom-2">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-sm font-bold text-white uppercase">Añadir Nuevo Color/Variante</h4>
                                                <button type="button" onClick={() => setIsAddingVariant(false)} className="text-zinc-500 hover:text-white"><X size={16} /></button>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] uppercase font-bold text-zinc-500">Apariencia (Color/Textura)</label>
                                                    <select
                                                        value={newVariantData.color_name}
                                                        onChange={(e) => {
                                                            const selected = STANDARD_COLORS.find(c => c.name === e.target.value);
                                                            if (selected) {
                                                                setNewVariantData({
                                                                    ...newVariantData,
                                                                    color_name: selected.name,
                                                                    color_code: selected.code,
                                                                    color_hex: selected.hex
                                                                });
                                                            } else {
                                                                setNewVariantData({ ...newVariantData, color_name: e.target.value });
                                                            }
                                                        }}
                                                        className="w-full bg-black border border-zinc-700 hover:border-zinc-600 hover:bg-zinc-900/50 text-white text-sm p-2 rounded focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200 outline-none cursor-pointer"
                                                    >
                                                        <option value="" disabled>Seleccione Color/Textura...</option>
                                                        {STANDARD_COLORS.map(c => (
                                                            <option key={c.name} value={c.name}>{c.label}</option>
                                                        ))}
                                                    </select>
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-1 bg-black border border-zinc-700 p-2 rounded text-zinc-400 text-sm flex items-center justify-between">
                                                            <span>Código: {newVariantData.color_code || '---'}</span>
                                                            <div
                                                                className="w-6 h-6 rounded-sm border border-zinc-600"
                                                                style={{ backgroundColor: newVariantData.color_hex }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] uppercase font-bold text-zinc-500">Añadir modelos iniciales</label>
                                                    <SearchableSelect
                                                        value={newVariantData.models}
                                                        onChange={(val) => setNewVariantData({ ...newVariantData, models: val as string[] })}
                                                        options={availableModelsForBrand}
                                                        placeholder={!formData.brand_id && !product?.make ? "Selecciona una marca arriba..." : "Seleccionar Modelos..."}
                                                        multiple={true}
                                                        className={`w-full ${!formData.brand_id && !product?.make ? 'opacity-50 pointer-events-none' : ''}`}
                                                    />
                                                    <p className="text-[10px] text-zinc-500 text-right">{newVariantData.models.length} modelos seleccionados</p>
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={handleCreateVariant}
                                                    className="bg-white text-black px-4 py-2 rounded text-xs font-bold uppercase hover:bg-zinc-200 transition-colors disabled:opacity-50"
                                                    disabled={!newVariantData.color_name || newVariantData.models.length === 0}
                                                >
                                                    Confirmar Variante
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="border border-zinc-800 border-dashed rounded-lg bg-black/10 p-8 text-center animate-in fade-in duration-200">
                                <Tag size={24} className="mx-auto text-zinc-700 mb-3" />
                                <h3 className="text-sm font-bold text-zinc-500 uppercase mb-1">Sección de Variantes Bloqueada</h3>
                                <p className="text-xs text-zinc-600">
                                    Por favor selecciona una <span className="text-zinc-400 font-bold">Marca</span> y <span className="text-zinc-400 font-bold">Categoría</span> arriba para empezar a gestionar colores e imágenes.
                                </p>
                            </div>
                        )}


                    </form>
                </div>

                <div className="p-2 border-t border-zinc-800 bg-zinc-950 sticky bottom-0 rounded-b-lg shrink-0">
                    <div className="flex flex-row items-center gap-1.5 w-full">
                        {!isCreateMode && onDelete && userRole === 'admin' ? (
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="flex-1 h-11 px-2 bg-red-600/10 text-red-500 border border-red-500/20 hover:bg-red-600 hover:text-white rounded-sm text-[10px] sm:text-xs font-bold uppercase transition-all whitespace-nowrap overflow-hidden flex items-center justify-center shrink-0"
                            >
                                <span className="sm:hidden">ELIMINAR</span>
                                <span className="hidden sm:inline">ELIMINAR ARTÍCULO</span>
                            </button>
                        ) : (
                            !isCreateMode && <div className="flex-1 sm:hidden h-11" />
                        )}

                        <button
                            onClick={onClose}
                            className="flex-1 h-11 px-2 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-white rounded-sm text-[10px] sm:text-xs font-bold uppercase transition-all whitespace-nowrap flex items-center justify-center"
                            disabled={isSaving}
                        >
                            CANCELAR
                        </button>

                        <button
                            onClick={handleSubmit}
                            disabled={isSaving || isUploading}
                            className="flex-1 h-11 px-2 bg-white hover:bg-zinc-200 text-black rounded-sm shadow-[0_0_15px_rgba(255,255,255,0.2)] text-[10px] sm:text-xs font-bold uppercase flex items-center justify-center gap-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            {isSaving ? (
                                <div className="w-3" />
                            ) : (
                                <Save size={14} className="shrink-0" />
                            )}
                            <div className="flex flex-col items-center">
                                <span className="sm:hidden">{isSaving ? '...' : 'GUARDAR'}</span>
                                <span className="hidden sm:inline">{isSaving ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}</span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="¿Eliminar Producto?"
                message={`Estás a punto de eliminar "${product?.name}". Esta acción borrará todas sus variantes y disponibilidad de modelos.`}
                confirmLabel="Sí, Eliminar"
                isDestructive={true}
                onConfirm={async () => {
                    if (product?.id && onDelete) {
                        await onDelete(product.id);
                        setShowDeleteConfirm(false);
                        onClose();
                    }
                }}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </div>
    );
};
